import { useState } from "react";
import { AlertCircle, Pencil, Plus, Trash2, Users } from "lucide-react";
import {
    Avatar,
    AvatarGradientFallback,
    AvatarImage,
    Button,
    Container,
    DataTable,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Field,
    FieldGroup,
    FieldLabel,
    FieldSet,
    Input,
    Mono,
    SectionHeader,
    TableSkeleton,
    Text,
    Textarea,
} from "@athena/ui";
import { useQuery, useMutation } from "@apollo/client/react";
import { toast } from "sonner";
import {
    PresentersDocument,
    UpdatePresenterDocument,
    DeletePresenterDocument,
    type PresentersQuery,
} from "../../graphql/presenters.generated";
import { NewPresentersDialog } from "./NewPresentersDialog";
import { DeleteDialog } from "@/components/DeleteDialog";
import { ImageUploader } from "@/components/ImageUploader";
import { PAGE_SIZE } from "../../../../global/constants";
import { useListView } from "@/hooks/useListView";
import { ListViewControls } from "@/components/tables/ListViewControls";
import { getInitialsFrom } from "../../../../global/formatHelpers";

type Presenter = PresentersQuery["presenters"][number];

export function PresentersPage() {
    const [
        search,
        setSearch,
    ] = useState<string>("");
    const [
        selected,
        setSelected,
    ] = useState<Set<string>>(new Set());
    const [
        newPresenterOpen,
        setNewPresenterOpen,
    ] = useState<boolean>(false);
    const [
        editPresenter,
        setEditPresenter,
    ] = useState<(Pick<Presenter, "id" | "name" | "title"> & { bio: string; thumbnailUrl?: string | null }) | null>(
        null,
    );
    const [
        editName,
        setEditName,
    ] = useState<string>("");
    const [
        editTitle,
        setEditTitle,
    ] = useState<string>("");
    const [
        editBio,
        setEditBio,
    ] = useState<string>("");
    /**
     * Tri-state, and each state means something different to the mutation:
     * `undefined` the photo was not touched, so `imageAssetId` is not sent at all; a string a new
     * photo was uploaded; `null` the operator removed it, which detaches without deleting the asset.
     *
     * There used to be an `editImagePreview` beside this. It was declared, reset twice, and never
     * set — which is why picking a photo appeared to do nothing. The uploader owns its own preview
     * now, so there is nothing here to forget to set.
     */
    const [
        editImageAssetId,
        setEditImageAssetId,
    ] = useState<string | null | undefined>(undefined);
    const [
        deletePresenter,
        setDeletePresenter,
    ] = useState<Pick<Presenter, "id" | "name" | "courses"> | null>(null);
    const { data, loading, error, refetch } = useQuery(PresentersDocument);
    const [
        updatePresenter,
        { loading: updating },
    ] = useMutation(UpdatePresenterDocument);
    const [
        deletePresenterMutation,
        { loading: deleting },
    ] = useMutation(DeletePresenterDocument);

    const presenters = data?.presenters ?? [];

    const filtered = presenters.filter((p) => {
        const matchesSearch =
            p.name.toLowerCase().includes(search.toLowerCase()) ||
            (p.title ?? "").toLowerCase().includes(search.toLowerCase());
        return matchesSearch;
    });

    /*
     * Scroll or pages, the reader's choice, portal-wide — see `useListView`. Above the guards
     * below because it calls hooks, which cannot sit after an early return.
     */
    const view = useListView(filtered, { pageSize: PAGE_SIZE });

    // Keyed on data, not `loading`: Apollo sets loading on every refetch, so gating
    // the whole page on it replaces live content — and unmounts any child holding
    // state or an in-flight request — every time something reloads.
    if (loading && !data) return <TableSkeleton />;
    if (error) return <PresentersPageError onRetry={refetch} />;

    const totalPresenters = presenters.length;
    const totalCredits = presenters.reduce((sum, p) => sum + p.courses.length, 0);

    const openEdit = (p: Presenter) => {
        setEditPresenter({ id: p.id, name: p.name, title: p.title, bio: p.bio ?? "", thumbnailUrl: p.thumbnailUrl });
        setEditName(p.name);
        setEditTitle(p.title ?? "");
        setEditBio(p.bio ?? "");
        // Cleared per open, or a photo picked for one presenter would be saved onto the next.
        setEditImageAssetId(undefined);
    };

    const handleUpdate = async () => {
        if (!editPresenter || !editName.trim()) return;
        await updatePresenter({
            variables: {
                id: editPresenter.id,
                input: {
                    name: editName.trim(),
                    title: editTitle.trim(),
                    bio: editBio.trim(),
                    // Sent only when touched — see the state's own note. `null` is meaningful here.
                    ...(editImageAssetId !== undefined ? { imageAssetId: editImageAssetId } : {}),
                },
            },
            optimisticResponse: {
                updatePresenter: {
                    __typename: "Presenter",
                    id: editPresenter.id,
                    name: editName.trim(),
                    title: editTitle.trim(),
                    bio: editBio.trim(),
                    // The photo URL is minted by the media service and cannot be guessed here, so
                    // the optimistic entry keeps the existing one and the real response replaces
                    // it. A removal is the one case we can predict, and showing the old photo
                    // through the save would contradict what the operator just did.
                    thumbnailUrl: editImageAssetId === null ? null : (editPresenter.thumbnailUrl ?? null),
                },
            },
        });
        setEditPresenter(null);
        setEditImageAssetId(undefined);
    };

    const deleteBlockedReason =
        deletePresenter && deletePresenter.courses.length > 0
            ? `Still assigned to ${deletePresenter.courses.length} course${deletePresenter.courses.length === 1 ? "" : "s"}: ${deletePresenter.courses.map((c) => c.title).join(", ")}. Remove it from ${deletePresenter.courses.length === 1 ? "that course" : "those courses"} first.`
            : null;

    const handleDelete = async () => {
        if (!deletePresenter) return;
        try {
            await deletePresenterMutation({
                variables: { id: deletePresenter.id },
                update(cache) {
                    cache.evict({ id: cache.identify({ __typename: "Presenter", id: deletePresenter.id }) });
                    cache.gc();
                },
            });
            setSelected((prev) => {
                const next = new Set(prev);
                next.delete(deletePresenter.id);
                return next;
            });
            setDeletePresenter(null);
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Couldn't delete this presenter. Please try again.");
        }
    };

    return (
        <Container className="p-6">
            <SectionHeader
                eyebrow={`Content · ${totalPresenters} Presenters · ${totalCredits} Course Credits`}
                title={"Presenters"}
                description={"People who appear in or lead Release U courses. Learners see them on the course page."}
                actions={
                    <Button size="sm" className="flex items-center gap-1.5" onClick={() => setNewPresenterOpen(true)}>
                        <Plus className="h-3.5 w-3.5" />
                        New Presenter
                    </Button>
                }
            />

            {/* Filters */}
            <div className="mb-4 flex items-center gap-3">
                <div className="relative max-w-xs flex-1">
                    <Users className="text-muted-foreground absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2" />
                    <Input
                        placeholder="Search presenters..."
                        className="bg-card h-9 pl-9 text-sm"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                        }}
                        type="text"
                    />
                </div>
            </div>

            {/* Table */}
            {/* Above the table, where Becca asked for it: "the top of the table row or above it". */}
            <ListViewControls
                view={view}
                noun="presenters"
                count={filtered.length === 0 ? "0 results" : `${view.from}–${view.to} of ${filtered.length}`}
            />

            <DataTable
                columns={[
                    {
                        key: "presenter",
                        header: "Presenter",
                        className: "min-w-50",
                        cell: (presenter) => (
                            <div className="flex items-center gap-3">
                                <Avatar size="sm" className="shrink-0">
                                    {presenter.thumbnailUrl && <AvatarImage src={presenter.thumbnailUrl} alt="" />}
                                    <AvatarGradientFallback>{getInitialsFrom(presenter.name)}</AvatarGradientFallback>
                                </Avatar>
                                <div className="min-w-0">
                                    <Text weight="semibold" className="truncate text-sm">
                                        {presenter.name}
                                    </Text>
                                    <Text muted className="max-w-45 truncate text-xs">
                                        {presenter.title}
                                    </Text>
                                </div>
                            </div>
                        ),
                    },
                    {
                        key: "bio",
                        header: "Bio",
                        className: "max-w-95",
                        cell: (presenter) => (
                            <Text muted className="line-clamp-2 text-xs leading-relaxed">
                                {presenter.bio}
                            </Text>
                        ),
                    },
                    {
                        key: "courses",
                        header: "Courses",
                        headerClassName: "text-right",
                        className: "text-right",
                        cell: (presenter) => <Mono className="text-sm">{presenter.courses.length}</Mono>,
                    },
                ]}
                rows={view.rows}
                rowKey={(presenter) => presenter.id}
                rowSelected={(presenter) => selected.has(presenter.id)}
                filtered
                noMatches="No presenters match your filters."
                actions={(presenter) => (
                    <div className="flex items-center justify-end gap-1">
                        <Button
                            variant="outline"
                            size="icon-sm"
                            className="text-muted-foreground hover:text-foreground"
                            aria-label={`Edit ${presenter.name}`}
                            onClick={() => openEdit(presenter)}
                        >
                            <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                            variant="outline"
                            size="icon-sm"
                            className="text-muted-foreground hover:text-destructive"
                            aria-label={`Delete ${presenter.name}`}
                            onClick={() => setDeletePresenter(presenter)}
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                )}
            />

            <div className="mt-4">
                <Mono className="text-muted-foreground text-xs">
                    {selected.size > 0 ? `${selected.size} selected` : ""}
                </Mono>
            </div>
            <NewPresentersDialog open={newPresenterOpen} onOpenChange={setNewPresenterOpen} />

            {/* Edit dialog */}
            <Dialog
                open={!!editPresenter}
                onOpenChange={(open) => {
                    if (!open) setEditPresenter(null);
                }}
            >
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Edit presenter</DialogTitle>
                        <DialogDescription>Update this presenter's details.</DialogDescription>
                    </DialogHeader>
                    <FieldSet>
                        <FieldGroup>
                            <Field>
                                <FieldLabel htmlFor="edit-presenter-name" required>
                                    Full name
                                </FieldLabel>
                                <Input
                                    id="edit-presenter-name"
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    autoFocus
                                    type="text"
                                />
                            </Field>
                            <Field>
                                <FieldLabel htmlFor="edit-presenter-title">Title / role</FieldLabel>
                                <Input
                                    id="edit-presenter-title"
                                    value={editTitle}
                                    onChange={(e) => setEditTitle(e.target.value)}
                                    type="text"
                                />
                            </Field>
                            <Field>
                                <FieldLabel htmlFor="edit-presenter-bio">Short bio</FieldLabel>
                                <Textarea
                                    id="edit-presenter-bio"
                                    value={editBio}
                                    onChange={(e) => setEditBio(e.target.value)}
                                    rows={3}
                                />
                            </Field>
                            {/*
                             * ATL-183. A presenter belongs to no organization, so this uploads
                             * with a null org — platform-wide, ADMIN/SYSTEM only. The upload
                             * stores the image immediately; the presenter only points at it when
                             * this dialog is saved, so cancelling leaves an unreferenced image
                             * rather than a changed presenter.
                             */}
                            <Field>
                                <FieldLabel>Photo</FieldLabel>
                                <ImageUploader
                                    organizationId={null}
                                    shape="circle"
                                    label={editPresenter?.thumbnailUrl ? "Replace photo" : "Upload photo"}
                                    currentUrl={editImageAssetId === null ? null : editPresenter?.thumbnailUrl}
                                    fallbackText={getInitialsFrom(editName)}
                                    onUploaded={(_key: string, imageId: string) => setEditImageAssetId(imageId)}
                                    onRemoved={() => setEditImageAssetId(null)}
                                />
                                {editImageAssetId === null && (
                                    <Text muted className="text-xs">
                                        Photo will be removed when you save.
                                    </Text>
                                )}
                            </Field>
                        </FieldGroup>
                    </FieldSet>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditPresenter(null)}>
                            Cancel
                        </Button>
                        <Button disabled={!editName.trim() || updating} onClick={handleUpdate}>
                            {updating ? "Saving…" : "Save"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <DeleteDialog
                open={!!deletePresenter}
                onOpenChange={(o) => {
                    if (!o) setDeletePresenter(null);
                }}
                title="Delete Presenter"
                deleteTarget={deletePresenter?.name}
                onConfirm={handleDelete}
                loading={deleting}
                blockedReason={deleteBlockedReason}
            />
        </Container>
    );
}

function PresentersPageError({ onRetry }: { onRetry: () => void }) {
    return (
        <Container className="p-6">
            <div className="flex flex-col items-center justify-center gap-3 py-32">
                <AlertCircle />
                <Text weight="medium">Failed to load presenters</Text>
                <Text muted className="text-sm">
                    Something went wrong. Please try again.
                </Text>
                <Button variant="outline" size="sm" onClick={onRetry}>
                    Retry
                </Button>
            </div>
        </Container>
    );
}
